import { Box, Button, Flex, Grid, Heading, Spinner, Text } from '@chakra-ui/react';
import { ChevronLeft } from 'lucide-react';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Navigate, useNavigate } from 'react-router-dom';
import { InternationalEventResponse } from 'api/responseTypes';
import { InternationalEventCard } from 'components/InternationalEventCard/InternationalEventCard';
import { useEventTracking } from 'hooks/useEventTracking';
import {
  useInternationalEventsQuery,
  useIsInternationalEventsEnabled,
} from 'pages/InternationalEvents/hooks/useInternationalEvents';
import { ROUTES } from 'router/routes';

/** How many events to preview in each country grid before "عرض الكل". */
const COUNTRY_PREVIEW_COUNT = 8;
const CONTENT_MAX_W = '1200px';

/** Group events by country, preserving first-seen order. Null country → "أخرى". */
const groupByCountry = (items: InternationalEventResponse[]) => {
  const groups = new Map<string, InternationalEventResponse[]>();
  for (const event of items) {
    const key = event.country ?? 'أخرى';
    const bucket = groups.get(key);
    if (bucket) bucket.push(event);
    else groups.set(key, [event]);
  }
  return [...groups.entries()];
};

/**
 * International events listing grouped by country — each country shows a header
 * with a count, a horizontal row of events, and a "عرض الكل" link to its page.
 */
export const InternationalEventsListingPage = () => {
  const navigate = useNavigate();
  const { eventTracking } = useEventTracking();
  const { isLoading, items } = useInternationalEventsQuery();
  const isEnabled = useIsInternationalEventsEnabled();

  useEffect(() => {
    eventTracking.page.viewed({
      pageName: 'international_events_listing',
      url: window.location.href,
    });
  }, [eventTracking]);

  if (!isEnabled) {
    return <Navigate to={ROUTES.home} replace />;
  }

  const handleItemClick = (event: InternationalEventResponse) => {
    eventTracking.internationalEvent.clicked({
      eventId: event.id,
      eventName: event.title,
      eventDate: event.date ?? null,
      venue: event.city ?? undefined,
      source: 'international_events_page',
    });
    navigate('/international-event/' + event.id);
  };

  const handleViewAll = (country: string) => {
    navigate(`/${ROUTES.internationalEvents}/${encodeURIComponent(country)}`);
  };

  const countries = groupByCountry(items);

  return (
    <Box bg={'white'} minH='100vh' py={{ base: 6, md: 10 }} dir='rtl'>
      <Box maxW={CONTENT_MAX_W} mx='auto' px={{ base: 5, md: 8 }}>
        <Heading
          as='h1'
          variant={{ base: 'headingMedium', md: 'headingLarge' }}
          mb={{ base: 6, md: 8 }}
        >
          <FormattedMessage
            defaultMessage='الفعاليات الدولية'
            id='homepage.international.heading'
          />
        </Heading>

        {isLoading ? (
          <Flex align='center' justify='center' py={16}>
            <Spinner color='green.G50' size='xl' thickness='4px' />
          </Flex>
        ) : countries.length === 0 ? (
          <Flex align='center' justify='center' py={16}>
            <Text textStyle='bodyLargeRegular' color='dark.D500'>
              <FormattedMessage
                defaultMessage='لا توجد فعاليات متاحة'
                id='homepage.events.noEvents'
              />
            </Text>
          </Flex>
        ) : (
          <Flex direction='column' gap={{ base: 8, md: 10 }}>
            {countries.map(([country, countryEvents]) => (
              <Box as='section' key={country} aria-label={country}>
                {/* Country header: title + count, and view-all */}
                <Flex align='center' justify='space-between' mb={{ base: 3, md: 4 }}>
                  <Flex align='baseline' gap={2}>
                    <Heading as='h2' variant={{ base: 'headingSmall', md: 'headingMedium' }}>
                      <FormattedMessage
                        defaultMessage='فعاليات {country}'
                        id='internationalEvents.countryEvents'
                        values={{ country }}
                      />
                    </Heading>
                    <Text textStyle='bodySmallRegular' color='dark.D500'>
                      ({countryEvents.length})
                    </Text>
                  </Flex>

                  {countryEvents.length > COUNTRY_PREVIEW_COUNT && (
                    <Button
                      variant='link'
                      color='green.G500'
                      fontWeight='600'
                      rightIcon={<ChevronLeft size={18} />}
                      onClick={() => handleViewAll(country)}
                    >
                      <FormattedMessage defaultMessage='عرض الكل' id='homepage.viewAll' />
                    </Button>
                  )}
                </Flex>

                {/* Card grid — 2 columns on mobile, more on larger screens */}
                <Grid
                  as='ul'
                  listStyleType='none'
                  templateColumns={{
                    base: 'repeat(2, minmax(0, 1fr))',
                    md: 'repeat(3, minmax(0, 1fr))',
                    lg: 'repeat(4, minmax(0, 1fr))',
                  }}
                  gap={{ base: 3, md: 5 }}
                  m={0}
                  p={0}
                >
                  {countryEvents.slice(0, COUNTRY_PREVIEW_COUNT).map((event) => (
                    <InternationalEventCard
                      key={event.id}
                      event={event}
                      onCardClick={() => handleItemClick(event)}
                    />
                  ))}
                </Grid>
              </Box>
            ))}
          </Flex>
        )}
      </Box>
    </Box>
  );
};
